import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InputMask from 'react-input-mask';
import { AlertCircle, Mail, Smartphone, Loader2, ArrowLeft } from 'lucide-react';
import { useValidationFlag } from '../components/hooks/useValidationFlag';

const LoadingTransitionMain = ({ title, message }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
    <div className="bg-gray-900 rounded-2xl p-8 shadow-2xl max-w-md w-full mx-4" style={{ border: '1px solid #22d3ee' }}>
      <div className="flex items-center justify-center mb-4">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#22d3ee' }} />
      </div>
      <h3 className="text-lg font-semibold text-white text-center mb-2">{title}</h3>
      <p className="text-gray-400 text-center">{message}</p>
    </div>
  </div>
);

const ValidationPage = () => {
  const navigate = useNavigate();
  const { set } = useValidationFlag();
  const { clear } = useValidationFlag();
  const [validationEmail, setValidationEmail] = useState('');
  const [validationMobile, setValidationMobile] = useState('');
  const [validationError, setValidationError] = useState('');
  const [validationConformation, setValidationConformation] = useState('');
  const [validationLoading, setValidationLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const userType = "Newuser"
  const companylogo = "/static/images/snl_dark_logo.png";
  const emailInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  
  const gradientTextStyle = {
    background: 'linear-gradient(135deg, #22d3ee 0%, #3b82f6 50%, #8b5cf6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    filter: 'drop-shadow(0 0 30px rgba(34, 211, 238, 0.3))'
  };

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const handleBack = () => {
    
      clear();
      navigate('/validlogin')
    
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    setValidationLoading(true);
    setValidationError('');
    setValidationConformation('');
    
    try {
      const response = await fetch('/api/validate-login-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: validationEmail,
          mobile: validationMobile,
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        set();
        setValidationConformation('Access accepted. Proceeding to onboarding control hub.');
        setShowSuccess(true);
        const result = data;
        setTimeout(() => {
          navigate('/landingprofile',  { state: { userType, data: result } });
        }, 3000);
      } else {
        setValidationError('Access denied. You are not authorized to use this form.');
        setTimeout(() => {
          setValidationEmail('');
          setValidationMobile('');
          setValidationError('');
          emailInputRef.current?.focus();
        }, 3000);
      }
    } catch (error) {
      setValidationError('Network error. Please try again.');
      setTimeout(() => {
        setValidationError('');
      }, 3000);
    } finally {
      setValidationLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center px-4">
      <div className="max-w-lg w-full space-y-8">
        <div className="bg-gray-900/50 rounded-3xl shadow-lg p-8" style={{border: '1px solid #22d3ee'}}>
          {/* Logo */}
          <div className="flex items-center justify-center">
            <img src={companylogo} alt="Company Logo" className="w-24" />
          </div>
          
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold" style={gradientTextStyle}>
              Validate Access
            </h2>
            <p className="mt-2 font-medium" style={gradientTextStyle}>
              Please enter your email and mobile number to access the central onboarding hub.
            </p>
          </div>

          {!showSuccess ? (
            <div className="space-y-6">
              {/* Email Input */}
              <div>
                <label htmlFor="validation-email" className="block text-sm font-medium" style={gradientTextStyle}>
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="validation-email"
                    ref={emailInputRef}
                    value={validationEmail}
                    onChange={(e) => setValidationEmail(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, () => mobileInputRef.current?.focus())}
                    placeholder="youremail@example.com"
                    className="appearance-none block w-full pl-10 pr-3 py-2 rounded-lg shadow-sm placeholder-gray-400 bg-gray-800 text-white border border-gray-700 focus:border-cyan-400 focus:outline-none"
                    required
                  />
                </div>
              </div>

              {/* Mobile Input */}
              <div>
                <label htmlFor="validation-mobile" className="block text-sm font-medium" style={gradientTextStyle}>
                  Mobile Number
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Smartphone className="h-5 w-5 text-gray-400" />
                  </div>
                  <InputMask
                    mask="(999) 999-9999"
                    type="tel"
                    id="validation-mobile"
                    inputRef={mobileInputRef}
                    value={validationMobile}
                    onChange={(e) => setValidationMobile(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, handleSubmit)}
                    placeholder="(000) 000-0000"
                    className="appearance-none block w-full pl-10 pr-3 py-2 rounded-lg shadow-sm placeholder-gray-400 bg-gray-800 text-white border border-gray-700 focus:border-cyan-400 focus:outline-none"
                    required
                  />
                </div>
              </div>

              {/* Error Message */}
              {validationError && (
                <div className="flex items-center space-x-2 text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>{validationError}</span>
                </div>
              )}

              {/* Success Message */}
              {validationConformation && (
                <div className="bg-green-900/20 border border-green-500/50 rounded-lg p-3">
                  <p className="text-green-400 text-sm text-center">{validationConformation}</p>
                </div>
              )}

              {/* Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleSubmit}
                  disabled={validationLoading || !validationEmail || !validationMobile}
                  className="w-full flex justify-center py-2 px-4 rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-black shadow-sm text-lg font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ border: '1px solid #22d3ee' }}
                >
                  {validationLoading ? (
                    <span className="flex items-center">
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Validating...
                    </span>
                  ) : (
                    'Validate Access'
                  )}
                </button>

                <button
                  onClick={handleBack}
                  disabled={validationLoading}
                  className="w-full flex items-center justify-center text-lg font-medium disabled:opacity-50"
                  style={ gradientTextStyle }
                >
                  <ArrowLeft className="w-4 h-4 mr-2" style={{ color: '#22d3ee' }} />
                  Back Home
                </button>
              </div>
            </div>
          ) : (
            /* Success State */
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center mb-4">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#22d3ee' }} />
              </div>
              <div className="w-16 h-16 bg-green-200/20 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium" style={gradientTextStyle}>
                Access Validated!
              </h3>
              <p className="text-sm" style={gradientTextStyle}>
                Redirecting to onboarding control hub...
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Loading Overlay */}
      {validationLoading && !showSuccess && (
        <LoadingTransitionMain 
          title="Validating Access"
          message="Please wait while we verify your credentials..."
        />
      )}
    </div>
  );
};

export default ValidationPage;